import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Persistent focus-music player.
 * - Web Audio gain node for fade in/out.
 * - Auto-advances tracks in continuous mode.
 * - `startTrackId` is the initial track (from theme settings).
 * - `onExposeControls(ctrl)` gives the parent a ref to { play, pauseFade, next, prev, setVolume, currentTrack }.
 */
export function useMusicPlayer({ initialVolume = 30, onExposeControls } = {}) {
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(initialVolume);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const srcNodeRef = useRef(null);
  const gainRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/music/tracks");
        setTracks(data.items || []);
      } catch {}
    })();
  }, []);

  const ensureGraph = () => {
    if (!audioRef.current) return;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      srcNodeRef.current = ctxRef.current.createMediaElementSource(audioRef.current);
      gainRef.current = ctxRef.current.createGain();
      gainRef.current.gain.value = volume / 100;
      srcNodeRef.current.connect(gainRef.current).connect(ctxRef.current.destination);
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
  };

  const fadeTo = (target, ms) => {
    if (!ctxRef.current || !gainRef.current) return;
    const now = ctxRef.current.currentTime;
    gainRef.current.gain.cancelScheduledValues(now);
    gainRef.current.gain.setValueAtTime(gainRef.current.gain.value || 0.0001, now);
    gainRef.current.gain.linearRampToValueAtTime(target, now + ms / 1000);
  };

  const playTrackByIndex = (i) => {
    if (!tracks.length || !audioRef.current) return;
    const idx = ((i % tracks.length) + tracks.length) % tracks.length;
    setCurrent(idx);
    const t = tracks[idx];
    const url = (process.env.REACT_APP_BACKEND_URL || "") + t.file_url;
    audioRef.current.src = url;
    audioRef.current.play().then(() => {
      setPlaying(true);
      ensureGraph();
      // Start silent then fade in
      if (gainRef.current) {
        gainRef.current.gain.value = 0.0001;
        fadeTo(volume / 100, 500);
      }
    }).catch(() => setPlaying(false));
  };

  const play = () => {
    if (!tracks.length) return;
    if (audioRef.current?.src) {
      audioRef.current.play().then(() => {
        setPlaying(true);
        ensureGraph();
        fadeTo(volume / 100, 500);
      }).catch(() => {});
    } else {
      playTrackByIndex(current);
    }
  };

  const pauseFade = (ms = 2000) => {
    if (!audioRef.current) return;
    ensureGraph();
    fadeTo(0.0001, ms);
    setTimeout(() => { try { audioRef.current.pause(); } catch {} setPlaying(false); }, ms + 50);
  };

  const next = () => playTrackByIndex(current + 1);
  const prev = () => playTrackByIndex(current - 1);
  const setVol = (v) => {
    setVolume(v);
    if (gainRef.current) gainRef.current.gain.value = v / 100;
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = () => playTrackByIndex(current + 1);
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [current, tracks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onExposeControls) onExposeControls({ play, pauseFade, next, prev, setVolume: setVol, playing, tracks, current });
  }, [tracks, current, playing]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    tracks, current, playing, volume,
    audioRef,
    play, pauseFade, next, prev, setVolume: setVol,
    playTrackByIndex,
    currentTrack: tracks[current],
  };
}

export function MiniPlayer({ player }) {
  const t = player.currentTrack;
  const artUrl = t?.artwork_url ? (process.env.REACT_APP_BACKEND_URL || "") + t.artwork_url : null;
  return (
    <div className="rounded-2xl p-3 fade-up" style={{ background: "color-mix(in oklab, var(--surface) 80%, transparent)", border: "1px solid var(--border)", backdropFilter: "blur(10px)" }} data-testid="music-mini-player">
      <audio ref={player.audioRef} preload="metadata" />
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden bg-cover bg-center"
          style={{
            backgroundImage: artUrl ? `url(${artUrl})` : "none",
            background: !artUrl ? "color-mix(in oklab, var(--primary-soft) 60%, transparent)" : undefined,
            border: "1px solid var(--border)",
          }}
          data-testid="music-mini-artwork"
          aria-hidden
        />
        <div className="flex-1 min-w-0 text-xs" style={{ color: "var(--text)" }}>
          <div className="truncate font-medium">{t?.title || "—"}</div>
          <div className="truncate text-muted">{t?.composer || ""}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button onClick={player.prev} className="btn-ghost !py-1 !px-2 text-xs" data-testid="music-prev-btn" aria-label="Previous">←</button>
        <button onClick={player.playing ? () => player.pauseFade(500) : player.play} className="btn-primary !py-1 !px-3 text-xs flex-1" data-testid="music-play-btn">
          {player.playing ? "Pause" : "Play"}
        </button>
        <button onClick={player.next} className="btn-ghost !py-1 !px-2 text-xs" data-testid="music-next-btn" aria-label="Next">→</button>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-[10px] uppercase tracking-wider text-muted">vol</span>
        <input type="range" min={0} max={100} value={player.volume} onChange={(e) => player.setVolume(Number(e.target.value))} className="flex-1 accent-[var(--primary)]" data-testid="music-volume-slider" />
        <span className="text-[10px] tabular-nums" style={{ color: "var(--text)" }}>{player.volume}</span>
      </div>
    </div>
  );
}
